import { useEffect } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * Development-only trace of who moves the user around.
 *
 * A screen that redirects the moment it renders leaves nothing behind to look
 * at: the address bar has already moved on, and the network log shows the
 * calls but not the decision that followed them. This prints both halves - the
 * route the app settled on, and every redirect a guard chose on the way - so a
 * flow that ends somewhere unexpected can be read back afterwards.
 *
 * Every export here compiles to a no-op in a production build.
 */

const enabled = process.env.NODE_ENV !== "production";

/**
 * Records a guard sending the user elsewhere, and why.
 *
 * Called during render, where the redirect is decided. React renders twice
 * under StrictMode in development, so a line appearing twice is the trace
 * doubling, not the app redirecting twice.
 */
export function traceRedirect(from: string, to: string, why: string): void {
  if (!enabled) return;
  console.warn(`[route] ${from} -> ${to} (${why})`);
}

/** Logs each route the app settles on, and whether it still has its state. */
const RouteTrace: React.FC = () => {
  const location = useLocation();
  const navigationType = useNavigationType();

  useEffect(() => {
    if (!enabled) return;
    console.info(
      `[route] ${navigationType} ${location.pathname}${location.search}`,
      {
        // Null here on a screen that was handed state is the thing to look
        // for: it means the history entry was replaced on the way in.
        state: location.state,
        key: location.key,
      }
    );
  }, [location, navigationType]);

  return null;
};

export default RouteTrace;
